import React, { useState } from "react";
import { feedbackApi } from "../api/routes";
import { apiErrorMessage } from "../api/client";
import { Button, Card, Input, Select, TextArea, Toast } from "../components/ui/Controls";
import { usePanelCache } from "../state/PanelCacheContext";

const TYPES = ["", "INFRASTRUCTURE", "COACHING", "WEBSITE", "MESSAGE", "PLAN", "BILLING", "ONBOARDING", "OTHER"];
const STATUSES = ["", "OPEN", "TRIAGED", "IN_PROGRESS", "RESOLVED", "WONT_DO"];
const SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

const formatDate = (value) => value ? new Date(value).toLocaleString() : "-";

export default function FeedbackPanel() {
  const [panelState, setPanelState] = usePanelCache("feedback");
  const [filters, setFilters] = useState(panelState.filters || { status: "OPEN", type: "", userId: "", limit: "100" });
  const [newRecord, setNewRecord] = useState({ userId: "", type: "OTHER", severity: "LOW", title: "", message: "" });
  const [selected, setSelected] = useState(panelState.selected || null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const records = panelState.records || [];

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ""));
      const res = await feedbackApi.list(params);
      const list = Array.isArray(res.data) ? res.data : [];
      setPanelState({ records: list, filters, selected: list[0] || null });
      setSelected(list[0] || null);
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error, "Unable to load feedback") });
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    if (!newRecord.message.trim()) return;
    setLoading(true);
    try {
      const res = await feedbackApi.create(newRecord);
      setToast({ message: "Feedback created" });
      setNewRecord({ userId: "", type: "OTHER", severity: "LOW", title: "", message: "" });
      const nextRecords = [res.data, ...records];
      setPanelState({ records: nextRecords, filters, selected: res.data });
      setSelected(res.data);
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error, "Unable to create feedback") });
    } finally {
      setLoading(false);
    }
  };

  const updateSelected = async (patch) => {
    if (!selected?.feedbackId) return;
    setLoading(true);
    try {
      const res = await feedbackApi.update(selected.feedbackId, patch);
      const updated = res.data;
      const nextRecords = records.map((record) => record.feedbackId === updated.feedbackId ? updated : record);
      setPanelState({ records: nextRecords, filters, selected: updated });
      setSelected(updated);
      setToast({ message: "Feedback updated" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error, "Unable to update feedback") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Card title="Feedback" action={<Button tone="ghost" onClick={load} loading={loading}>Refresh</Button>}>
        <Card title="Filters">
          <div className="form-grid">
            <Select label="Status" value={filters.status} onChange={(status) => setFilters({ ...filters, status })} options={STATUSES} />
            <Select label="Type" value={filters.type} onChange={(type) => setFilters({ ...filters, type })} options={TYPES} />
            <Input label="User ID" value={filters.userId} onChange={(userId) => setFilters({ ...filters, userId })} />
            <Input label="Limit" type="number" value={filters.limit} onChange={(limit) => setFilters({ ...filters, limit })} />
          </div>
          <Button onClick={load} loading={loading}>Search feedback</Button>
        </Card>

        <Card title="Create feedback">
          <div className="form-grid">
            <Input label="User ID" value={newRecord.userId} onChange={(userId) => setNewRecord({ ...newRecord, userId })} />
            <Select label="Type" value={newRecord.type} onChange={(type) => setNewRecord({ ...newRecord, type })} options={TYPES.filter(Boolean)} />
            <Select label="Severity" value={newRecord.severity} onChange={(severity) => setNewRecord({ ...newRecord, severity })} options={SEVERITIES} />
            <Input label="Title" value={newRecord.title} onChange={(title) => setNewRecord({ ...newRecord, title })} />
          </div>
          <TextArea label="Message" rows={4} value={newRecord.message} onChange={(message) => setNewRecord({ ...newRecord, message })} />
          <Button onClick={create} disabled={loading || !newRecord.message.trim()}>Create record</Button>
        </Card>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 360px), 1fr))", gap: 16 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
              <thead>
                <tr style={{ background: "rgba(25,164,99,0.08)" }}>
                  <th style={{ padding: 8, textAlign: "left" }}>Created</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Status</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Type</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Severity</th>
                  <th style={{ padding: 8, textAlign: "left" }}>Title</th>
                  <th style={{ padding: 8, textAlign: "left" }}>User</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr
                    key={record.feedbackId}
                    onClick={() => setSelected(record)}
                    style={{ borderBottom: "1px solid rgba(15,23,42,0.06)", cursor: "pointer", background: selected?.feedbackId === record.feedbackId ? "rgba(25,164,99,0.08)" : "transparent" }}
                  >
                    <td style={{ padding: 8, fontSize: 12 }}>{formatDate(record.createdAt)}</td>
                    <td style={{ padding: 8, fontWeight: 700 }}>{record.status || "-"}</td>
                    <td style={{ padding: 8 }}>{record.type || "-"}</td>
                    <td style={{ padding: 8 }}>{record.severity || "-"}</td>
                    <td style={{ padding: 8 }}>{record.title || "-"}</td>
                    <td style={{ padding: 8, fontFamily: "monospace", fontSize: 12 }}>{record.userId || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!records.length && <div className="empty-state">No feedback records found.</div>}
          </div>

          <Card title="Selected feedback">
            {selected ? (
              <>
                <div className="form-grid">
                  <Select label="Status" value={selected.status || "OPEN"} onChange={(status) => updateSelected({ status })} options={STATUSES.filter(Boolean)} />
                  <Select label="Type" value={selected.type || "OTHER"} onChange={(type) => updateSelected({ type })} options={TYPES.filter(Boolean)} />
                  <Select label="Severity" value={selected.severity || "LOW"} onChange={(severity) => updateSelected({ severity })} options={SEVERITIES} />
                </div>
                <TextArea label="Admin notes" rows={4} value={selected.adminNotes || ""} onChange={(adminNotes) => setSelected({ ...selected, adminNotes })} />
                <Button tone="secondary" onClick={() => updateSelected({ adminNotes: selected.adminNotes || "" })} disabled={loading}>Save notes</Button>
                <div style={{ marginTop: 16, display: "grid", gap: 8, fontSize: 13 }}>
                  <strong>{selected.title || "Untitled feedback"}</strong>
                  <span>{selected.message || "-"}</span>
                  <span style={{ color: "var(--stone-700)" }}>Source: {selected.source || "-"} · Updated {formatDate(selected.updatedAt)}</span>
                  <span style={{ fontFamily: "monospace", fontSize: 12 }}>{selected.feedbackId}</span>
                </div>
              </>
            ) : (
              <div className="empty-state">Select a feedback record to manage it.</div>
            )}
          </Card>
        </div>
      </Card>
      <Toast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}
